import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import './lib/arrayExtensions';

import {
  AdminAlphaAllowListPane,
  AdminBankConnectionsPane,
  AdminBillManagmentPane,
  AdminChatPane,
  AdminEnvironmentPane,
  AdminImpersonateUserPane,
  ProcessorStatusesPane,
  TransactionAnomalyFeedback,
  UsersWithTransactionReviewsPane,
} from './components/admin';
import AdminSentryTestPane from './components/admin/AdminSentryTestPane';
import ClarityRouteTracker from './components/ClarityRouteTracker';
import ErrorBoundary from './components/ErrorBoundary';
import MixpanelIdentifier from './components/MixpanelIdentifier';
import ScrollToTop from './components/ScrollToTop';
import {
  AccountOverviewPane,
  AccountPane,
  DashboardPane,
  MerchantPane,
  ObligationsPane,
  RecurringTransactionsPane,
  SettingsPane,
  SpendingPane,
} from './panes';
import { PATHS } from './paths';
import AuthProvider from './providers/AuthProvider';
import MarkerProvider from './providers/MarkerProvider';
import TransactionsProvider from './providers/TransactionsProvider';
import { HomeScene, LoginScene, OnboardingScene } from './scenes';

const removeLeadingSlash = (path: string): string => {
  return path.startsWith('/') ? path.slice(1) : path;
};

const MainApp = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <MarkerProvider>
            <ClarityRouteTracker />
            <MixpanelIdentifier />
            <ScrollToTop />
            <TransactionsProvider>
              <Routes>
                <Route path={PATHS.HOME} element={<HomeScene />}>
                  <Route path={removeLeadingSlash(PATHS.ACCOUNTS)} element={<AccountOverviewPane />} />
                  <Route path={`${removeLeadingSlash(PATHS.ACCOUNTS)}/:accountId`} element={<AccountPane />} />
                  <Route path={`${removeLeadingSlash(PATHS.MERCHANTS)}/:merchantId`} element={<MerchantPane />} />
                  <Route path={removeLeadingSlash(PATHS.PROCESSOR_STATUSES)} element={<ProcessorStatusesPane />} />
                  <Route
                    path={`${removeLeadingSlash(PATHS.TRANSACTION_ANOMALY_FEEDBACK)}/:userId`}
                    element={<TransactionAnomalyFeedback />}
                  />
                  <Route
                    path={removeLeadingSlash(PATHS.ADMIN_ALPHA_ALLOW_LIST)}
                    element={<AdminAlphaAllowListPane />}
                  />
                  <Route
                    path={removeLeadingSlash(PATHS.ADMIN_IMPERSONATE_USER)}
                    element={<AdminImpersonateUserPane />}
                  />
                  <Route
                    path={removeLeadingSlash(PATHS.ADMIN_BANK_CONNECTIONS)}
                    element={<AdminBankConnectionsPane />}
                  />
                  <Route path={removeLeadingSlash(PATHS.ADMIN_BILL_MANAGMENT)} element={<AdminBillManagmentPane />} />
                  <Route path={removeLeadingSlash(PATHS.ADMIN_SENTRY_TEST)} element={<AdminSentryTestPane />} />
                  <Route path={removeLeadingSlash(PATHS.ADMIN_ENVIRONMENT)} element={<AdminEnvironmentPane />} />
                  <Route path={removeLeadingSlash(PATHS.DASHBOARD)} element={<DashboardPane />} />
                  <Route
                    path={removeLeadingSlash(PATHS.ADMIN_USERS_WITH_TRANSACTION_REVIEWS)}
                    element={<UsersWithTransactionReviewsPane />}
                  />
                  <Route path={removeLeadingSlash(PATHS.ADMIN_CHAT)} element={<AdminChatPane />} />
                  <Route path={removeLeadingSlash(PATHS.SPENDING())} element={<SpendingPane />} />
                  <Route
                    path={removeLeadingSlash(PATHS.RECURRING_TRANSACTIONS)}
                    element={<RecurringTransactionsPane />}
                  />
                  <Route path={removeLeadingSlash(PATHS.OBLIGATIONS)} element={<ObligationsPane />} />
                  <Route path={removeLeadingSlash(PATHS.SETTINGS)} element={<SettingsPane />} />
                  <Route index element={<Navigate to={PATHS.DASHBOARD} replace />} />
                </Route>

                <Route path={PATHS.LOGIN} element={<LoginScene />} />
                <Route path={PATHS.ONBOARDING} element={<OnboardingScene />} />
                <Route path="*" element={<Navigate to={PATHS.DASHBOARD} replace />} />
              </Routes>
            </TransactionsProvider>
          </MarkerProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default MainApp;
